import { useTranslation } from 'react-i18next'
import type { BulletKey } from './channel-constants'

interface Props {
  mappedBulletKeys: BulletKey[]
  bulletFields: Record<BulletKey, string>
  onUpdateBullet: (key: BulletKey, value: string) => void
  keywordsMapped: boolean
  keywordsValue: string
  onUpdateKeywords: (value: string) => void
  isMasterChannel: boolean
}

const TEXTAREA_CLASS =
  'w-full text-sm px-3 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100'
const LABEL_CLASS = 'block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1'

export function ChannelMappedFieldsSection({
  mappedBulletKeys,
  bulletFields,
  onUpdateBullet,
  keywordsMapped,
  keywordsValue,
  onUpdateKeywords,
  isMasterChannel,
}: Props) {
  const { t } = useTranslation()
  const title = (
    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
      {t('productContent.channels.bullets.sectionTitle')}
    </h3>
  )

  if (isMasterChannel) {
    return (
      <section data-testid="channel-mapped-fields" className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
        {title}
        <p className="text-xs text-neutral-500 mt-2">{t('productContent.channels.bullets.masterUnavailable')}</p>
      </section>
    )
  }

  if (mappedBulletKeys.length === 0 && !keywordsMapped) {
    return (
      <section data-testid="channel-mapped-fields" className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
        {title}
        <p className="text-xs text-neutral-500 mt-2">{t('productContent.channels.bullets.notMappedHint')}</p>
      </section>
    )
  }

  return (
    <section data-testid="channel-mapped-fields" className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 space-y-4">
      {title}
      {mappedBulletKeys.map((key) => (
        <div key={key}>
          <label htmlFor={`bullet-${key}`} className={LABEL_CLASS}>
            {t('productContent.channels.bullets.bullet', { n: Number(key.slice(-1)) })}
          </label>
          <textarea
            id={`bullet-${key}`}
            rows={2}
            value={bulletFields[key] ?? ''}
            onChange={(e) => onUpdateBullet(key, e.target.value)}
            className={TEXTAREA_CLASS}
          />
        </div>
      ))}
      {keywordsMapped && (
        <div>
          <label htmlFor="bullet-keywords" className={LABEL_CLASS}>
            {t('productContent.channels.seoMetaKeywords')}
          </label>
          <textarea
            id="bullet-keywords"
            rows={2}
            value={keywordsValue}
            onChange={(e) => onUpdateKeywords(e.target.value)}
            className={TEXTAREA_CLASS}
          />
        </div>
      )}
    </section>
  )
}
